import { useState, useEffect, useCallback, useRef, type Ref, memo } from 'react';
import {
  ChevronDown,
  Calendar,
  RefreshCw,
  Eye,
  X,
  AlertTriangle,
  Shield,
  SlidersHorizontal,
  Sun,
  Moon,
  MoreHorizontal,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { authFetch, API_BASE } from '../../services/api/client';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth } from '@/contexts/AuthContext';
import { SilasButton } from '@/components/silas/SilasButton';
import { SilasSeal } from '@/components/silas/SilasSeal';
import { DataOpsStatusButton } from './DataOpsStatusButton';
import { BugReportButton } from './BugReportButton';
import { useTheme } from '@/contexts/ThemeContext';
import {
  useAutonomySettingsQuery,
  queryKeys,
  type AutonomySettingsResponse,
} from '@/hooks/queries/usePropertyQueries';

// Note: Popover components still used for Hybrid/Autonomous guardrail popovers

interface Property {
  id: string;
  name: string;
  units: number;
  readOnly?: boolean;
}

export interface SafetyRails {
  maxChangePct: number; // ±X% max change per update
  floorRate: number; // Minimum rate guardrail
  ceilingRate: number; // Maximum rate guardrail
  requireHealthyData: boolean; // Only approve if data health is green
  excludeEventDays: boolean; // Exclude event days (require manual review)
  minConfidence: number; // Minimum confidence threshold (0-100)
}

const DEFAULT_SAFETY_RAILS: SafetyRails = {
  maxChangePct: 10,
  floorRate: 89,
  ceilingRate: 499,
  requireHealthyData: true,
  excludeEventDays: true,
  minConfidence: 70,
};

export type AutonomyMode = 'manual' | 'hybrid' | 'autonomous';

const losModeForVisibleAutonomyToggle = (mode: AutonomyMode): AutonomyMode =>
  mode === 'manual' ? 'manual' : 'hybrid';

interface ScopeBarProps {
  property: Property;
  properties: Property[];
  onPropertyChange: (id: string) => void;
  dateRange: { start: string; end: string };
  onDateRangeChange: (range: { start: string; end: string }) => void;
  kpis: {
    occupancy: number;
    adr: number;
    revenue?: number;
    revpar?: number;
    pickup7dRevenue?: number;
  };
  isLoading: boolean;
  kpiError?: boolean;
  kpiUpdatedAt?: number;
  onRefreshKpis?: () => void;
  /** Hide the "all" portfolio option from property selector */
  hideAllOption?: boolean;
  /** Callback to open Ask Silas. Kept for older call sites that named this as the palette. */
  onOpenCommandPalette?: () => void;
  /** Callback to open Ask Silas. */
  onAskSilas?: () => void;
  /** Ref for Ask Silas button (used for greeting animation) */
  askSilasRef?: Ref<HTMLButtonElement>;
  /** Glow state for Ask Silas button */
  askSilasGlowing?: boolean;
  /** Show rate-specific autonomy and horizon controls in the shell. */
  showRateControls?: boolean;
}

export const ScopeBar = memo(function ScopeBar({
  property,
  properties,
  onPropertyChange,
  dateRange,
  isLoading,
  hideAllOption = false,
  onOpenCommandPalette,
  onAskSilas,
  askSilasRef,
  askSilasGlowing = false,
  showRateControls = true,
}: ScopeBarProps) {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isReadOnlyProperty = Boolean(property.readOnly);
  const canEditAutonomy = user?.role === 'super_admin' && !isReadOnlyProperty;

  // Lightweight Render Counter for Debugging
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const renderWindow = window as Window & { __renders?: Record<string, number> };
    renderWindow.__renders = renderWindow.__renders ?? {};
    renderWindow.__renders.ScopeBar = (renderWindow.__renders.ScopeBar ?? 0) + 1;
  }

  // Filter out "all" option if hideAllOption is true
  const filteredProperties = hideAllOption ? properties.filter((p) => p.id !== 'all') : properties;
  const [showAutonomyModal, setShowAutonomyModal] = useState(false);
  const [notifyOnAuto, setNotifyOnAuto] = useState(true);
  const [safetyRails, setSafetyRails] = useState<SafetyRails>(DEFAULT_SAFETY_RAILS);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingMode, setPendingMode] = useState<AutonomyMode>('manual');
  const [pendingLosMode, setPendingLosMode] = useState<AutonomyMode>('manual');
  const [autonomyNotice, setAutonomyNotice] = useState<string | null>(null);
  const autonomyGroupRef = useRef<HTMLDivElement>(null);

  // Single source of truth for the runtime autonomy mode: the shared React
  // Query cache entry. ScopeBar reads it directly (no prop, no parent copy),
  // so every route that renders a ScopeBar shows the same live value and
  // pages are structurally incapable of disagreeing about it. The query is
  // disabled for the portfolio scope ('all'), where no single mode exists, and
  // for watch-only properties where rate automation is intentionally off.
  const autonomySettingsQuery = useAutonomySettingsQuery(property.id, {
    enabled: showRateControls && !isReadOnlyProperty,
  });
  const autonomyData = autonomySettingsQuery.isPlaceholderData
    ? undefined
    : autonomySettingsQuery.data;
  const isPortfolioScope = property.id === 'all';
  const autonomyResolved = autonomyData != null;
  // null = not yet resolved (loading, error, or portfolio scope). The toggle
  // shows no active segment rather than guessing "manual".
  const autonomyMode: AutonomyMode | null = autonomyData
    ? (autonomyData.rate_mode ?? autonomyData.autonomy_mode)
    : null;
  const losMode: AutonomyMode | null = autonomyData ? (autonomyData.los_mode ?? 'manual') : null;
  const trustState = autonomyData?.trust_state ?? null;
  const autonomyBlockedByTrustState = Boolean(trustState?.block_autonomy);
  const trustStateReason =
    trustState?.reasons?.[0] ?? 'Cold start is active. Manual mode is required for now.';
  const autonomyAdminNotice = isReadOnlyProperty
    ? 'Rate controls are disabled for this property.'
    : !canEditAutonomy
      ? 'Autonomy changes are admin-only. You can review the current runtime mode, but only a super admin can change it.'
      : null;

  useEffect(() => {
    setNotifyOnAuto(true);
    setSafetyRails(DEFAULT_SAFETY_RAILS);
    setPendingLosMode('manual');
    setAutonomyNotice(null);
  }, [property.id]);

  // Sync modal draft state when query data arrives or changes
  useEffect(() => {
    if (!autonomyData) return;
    if (autonomyData.safety_rails) {
      setSafetyRails({
        maxChangePct: autonomyData.safety_rails.max_change_pct ?? DEFAULT_SAFETY_RAILS.maxChangePct,
        floorRate: autonomyData.safety_rails.floor_rate ?? DEFAULT_SAFETY_RAILS.floorRate,
        ceilingRate: autonomyData.safety_rails.ceiling_rate ?? DEFAULT_SAFETY_RAILS.ceilingRate,
        requireHealthyData:
          autonomyData.safety_rails.require_healthy_data ?? DEFAULT_SAFETY_RAILS.requireHealthyData,
        excludeEventDays:
          autonomyData.safety_rails.exclude_event_days ?? DEFAULT_SAFETY_RAILS.excludeEventDays,
        minConfidence:
          autonomyData.safety_rails.min_confidence ?? DEFAULT_SAFETY_RAILS.minConfidence,
      });
    }
    if (autonomyData.notify_on_auto !== undefined) {
      setNotifyOnAuto(autonomyData.notify_on_auto);
    }
  }, [autonomyData]);

  // Sync pending mode with current mode when modal opens
  useEffect(() => {
    if (showAutonomyModal) {
      setPendingMode(autonomyMode ?? 'manual');
      setPendingLosMode(losMode ?? 'manual');
    }
  }, [showAutonomyModal, autonomyMode, losMode]);

  const invalidateAutonomyCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.autonomySettings(property.id) });
  }, [property.id, queryClient]);

  // Write the accepted modes into the shared cache so every consumer flips
  // at once, then refetch to confirm against server truth.
  const applyAutonomyModes = useCallback(
    (nextMode: AutonomyMode, nextLosMode: AutonomyMode) => {
      queryClient.setQueryData<AutonomySettingsResponse>(
        queryKeys.autonomySettings(property.id),
        (old) =>
          old
            ? { ...old, autonomy_mode: nextMode, rate_mode: nextMode, los_mode: nextLosMode }
            : old,
      );
      invalidateAutonomyCache();
    },
    [invalidateAutonomyCache, property.id, queryClient],
  );

  const getAutonomyErrorReason = async (res: Response) => {
    const errorData = await res.json().catch(() => ({}));
    return (
      errorData?.detail?.trust_state?.reasons?.[0] ??
      errorData?.detail?.message ??
      errorData?.detail ??
      'Failed to update autonomy settings.'
    );
  };

  const handleQuickAutonomyChange = useCallback(
    async (nextMode: AutonomyMode) => {
      if (!canEditAutonomy) {
        setAutonomyNotice(autonomyAdminNotice);
        return;
      }

      // Never write from an unresolved state; the toggle is inert until the
      // runtime mode is known.
      if (!autonomyResolved) return;

      if (autonomyBlockedByTrustState && nextMode !== 'manual') {
        setAutonomyNotice(trustStateReason);
        return;
      }

      setAutonomyNotice(null);
      const nextLosMode = losModeForVisibleAutonomyToggle(nextMode);

      try {
        const res = await authFetch(`${API_BASE}/api/settings/autonomy`, {
          method: 'POST',
          body: JSON.stringify({
            property_id: property.id,
            autonomy_mode: nextMode,
            rate_mode: nextMode,
            los_mode: nextLosMode,
          }),
        });

        if (res.ok) {
          applyAutonomyModes(nextMode, nextLosMode);
          setPendingLosMode(nextLosMode);
          return;
        }

        const nextReason = await getAutonomyErrorReason(res);
        setAutonomyNotice(nextReason);
        if (res.status === 409) {
          invalidateAutonomyCache();
          return;
        }

        console.error(`Failed to save autonomy settings: ${res.status}`);
      } catch (err) {
        console.error('Failed to save autonomy settings:', err);
        setAutonomyNotice('Failed to update autonomy settings.');
      }
    },
    [
      applyAutonomyModes,
      autonomyBlockedByTrustState,
      autonomyResolved,
      canEditAutonomy,
      autonomyAdminNotice,
      invalidateAutonomyCache,
      property.id,
      trustStateReason,
    ],
  );

  const openAutonomySettings = useCallback(() => {
    setShowAutonomyModal(true);
  }, []);

  const handleSaveSettings = async () => {
    if (!canEditAutonomy) {
      setAutonomyNotice(autonomyAdminNotice);
      return;
    }

    if (autonomyBlockedByTrustState && (pendingMode !== 'manual' || pendingLosMode !== 'manual')) {
      setAutonomyNotice(trustStateReason);
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        property_id: property.id,
        autonomy_mode: pendingMode,
        rate_mode: pendingMode,
        los_mode: pendingLosMode,
        notify_on_auto: notifyOnAuto,
        safety_rails: {
          max_change_pct: safetyRails.maxChangePct,
          floor_rate: safetyRails.floorRate,
          ceiling_rate: safetyRails.ceilingRate,
          require_healthy_data: safetyRails.requireHealthyData,
          exclude_event_days: safetyRails.excludeEventDays,
          min_confidence: safetyRails.minConfidence,
        },
      };
      const res = await authFetch(`${API_BASE}/api/settings/autonomy`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setAutonomyNotice(null);
        applyAutonomyModes(pendingMode, pendingLosMode);
        setShowAutonomyModal(false);
      } else if (res.status === 409) {
        const errorData = await res.json().catch(() => ({}));
        const nextReason =
          errorData?.detail?.trust_state?.reasons?.[0] ??
          errorData?.detail?.message ??
          trustStateReason;
        setAutonomyNotice(nextReason);
      }
    } catch (err) {
      console.error('Failed to save autonomy settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Generate safety rails summary text
  const getSafetyRailsSummary = () => {
    const parts: string[] = [];
    parts.push(`±${safetyRails.maxChangePct}% max`);
    parts.push(`$${safetyRails.floorRate} floor`);
    if (safetyRails.excludeEventDays) parts.push('events excluded');
    if (safetyRails.requireHealthyData) parts.push('healthy data required');
    return parts.join(', ');
  };

  const formatDateRange = () => {
    if (!dateRange.start) return 'Loading...';
    const start = new Date(dateRange.start);
    const formatOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', formatOpts)} + 2yr`;
  };

  const losLabel =
    losMode === 'autonomous'
      ? 'Auto'
      : losMode === 'hybrid'
        ? 'Assist'
        : losMode === 'manual'
          ? 'Manual'
          : '—';
  const losTone =
    losMode === 'autonomous'
      ? 'text-accent-muted'
      : losMode === 'hybrid'
        ? 'text-warning'
        : losMode === 'manual'
          ? 'text-[var(--text-primary)]'
          : 'text-[var(--text-muted)]';

  const autonomyUnavailableTitle = isReadOnlyProperty
    ? 'Rate controls are disabled for this property.'
    : isPortfolioScope
      ? 'Autonomy is set per property. Select a property to view or change it.'
      : autonomySettingsQuery.isError
        ? 'Autonomy mode unavailable right now. Refresh to retry.'
        : !autonomyResolved
          ? 'Loading autonomy mode'
          : null;
  const handleAskSilas = onAskSilas ?? onOpenCommandPalette;

  // Roving tabindex for the radio group: the checked segment is the tab stop;
  // before the mode resolves, Manual is the entry point so the group stays
  // reachable (selection still requires Enter/Space on an enabled radio).
  const autonomyRadioTabIndex = (segment: AutonomyMode) =>
    autonomyMode === segment || (autonomyMode === null && segment === 'manual') ? 0 : -1;

  const handleAutonomyGroupKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const radios = Array.from(
      autonomyGroupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]') ?? [],
    );
    if (radios.length === 0) return;
    const current = radios.indexOf(document.activeElement as HTMLButtonElement);
    let next: number;
    if (e.key === 'ArrowRight') {
      next = current < 0 ? 0 : (current + 1) % radios.length;
    } else if (e.key === 'ArrowLeft') {
      next = current < 0 ? radios.length - 1 : (current - 1 + radios.length) % radios.length;
    } else if (e.key === 'Home') {
      next = 0;
    } else {
      next = radios.length - 1;
    }
    radios[next]?.focus();
    e.preventDefault();
  }, []);

  return (
    <>
      <div className="h-14 bg-[var(--bg)] border-b border-[var(--border-color)] flex items-center px-4 shrink-0 relative">
        {/* Left section - property selector + autonomy toggle */}
        <div className="flex items-center space-x-3">
          {/* Property Selector - compact, matches nav item styling */}
          <div className="relative">
            <select
              value={property.id}
              onChange={(e) => onPropertyChange(e.target.value)}
              className="appearance-none bg-[var(--surface)] border border-[var(--border-color)] rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-[var(--text-primary)] cursor-pointer hover:border-[var(--accent)] focus:outline-none focus:border-[var(--accent)] focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {filteredProperties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none" />
          </div>

          {showRateControls && (
            <>
              {/* Autonomy Mode Toggle */}
              <div className="flex items-center">
                <div
                  ref={autonomyGroupRef}
                  role="radiogroup"
                  aria-label="Rate autonomy mode"
                  aria-busy={
                    !isReadOnlyProperty &&
                    !autonomyResolved &&
                    !isPortfolioScope &&
                    !autonomySettingsQuery.isError
                  }
                  onKeyDown={handleAutonomyGroupKeyDown}
                  className="flex rounded-lg border border-[var(--border-color)] overflow-hidden bg-surface"
                >
                  <div className="hidden md:flex items-center px-2 border-r border-[var(--border-color)] text-[10px] font-semibold uppercase tracking-wide text-[var(--text-label)]">
                    Rates
                  </div>
                  {/* Manual */}
                  <button
                    role="radio"
                    aria-checked={autonomyMode === 'manual'}
                    tabIndex={autonomyRadioTabIndex('manual')}
                    aria-disabled={!canEditAutonomy || !autonomyResolved}
                    onClick={() => {
                      void handleQuickAutonomyChange('manual');
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-r border-[var(--border-color)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                      autonomyMode === 'manual'
                        ? 'bg-[var(--surface-hover)] text-[var(--text-primary)] shadow-[inset_0_0_0_1px_var(--text-muted)]'
                        : !canEditAutonomy
                          ? 'bg-transparent text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                          : 'bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]'
                    }`}
                    title={
                      autonomyUnavailableTitle ??
                      (canEditAutonomy
                        ? 'Review all rate decisions'
                        : 'Autonomy changes are admin-only')
                    }
                  >
                    <Eye className="w-4 h-4" />
                    {autonomyMode === 'manual' && (
                      <span
                        aria-hidden="true"
                        className="w-1.5 h-1.5 rounded-full bg-[var(--text-secondary)]"
                      />
                    )}
                    <span>Manual</span>
                  </button>

                  {/* Hybrid */}
                  <div
                    className={`flex items-center border-r border-[var(--border-color)] transition-colors ${
                      autonomyMode === 'hybrid'
                        ? 'bg-[var(--accent-dim)] shadow-[inset_0_0_0_1px_var(--accent)]'
                        : ''
                    }`}
                  >
                    <button
                      role="radio"
                      aria-checked={autonomyMode === 'hybrid'}
                      tabIndex={autonomyRadioTabIndex('hybrid')}
                      aria-disabled={!canEditAutonomy || !autonomyResolved}
                      onClick={() => {
                        void handleQuickAutonomyChange('hybrid');
                      }}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                        autonomyMode === 'hybrid'
                          ? 'text-[var(--accent)]'
                          : !canEditAutonomy
                            ? 'text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                            : autonomyBlockedByTrustState
                              ? 'text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                              : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                      title={
                        autonomyUnavailableTitle ??
                        (!canEditAutonomy
                          ? 'Autonomy changes are admin-only'
                          : autonomyBlockedByTrustState
                            ? trustStateReason
                            : 'Auto-approve safe changes')
                      }
                    >
                      {autonomyMode === 'hybrid' && (
                        <span
                          aria-hidden="true"
                          className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]"
                        />
                      )}
                      <span>Hybrid</span>
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          disabled={!canEditAutonomy}
                          className={`hidden md:flex min-h-9 min-w-9 items-center justify-center bg-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                            !canEditAutonomy
                              ? 'text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                              : autonomyMode === 'hybrid'
                                ? 'text-[var(--accent)]'
                                : 'text-[var(--text-muted)] hover:text-[var(--accent)]'
                          }`}
                          title={
                            canEditAutonomy
                              ? 'View hybrid guardrails'
                              : 'Autonomy changes are admin-only'
                          }
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-64 bg-surface-raised border-[var(--border-color)] p-3"
                        align="start"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <AlertTriangle className="w-4 h-4 text-warning" />
                          <span className="font-medium text-[var(--text-primary)] text-sm">
                            Hybrid Guardrails
                          </span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Base max change</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              ±{safetyRails.maxChangePct}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Rate floor</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              ${safetyRails.floorRate}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Rate ceiling</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              ${safetyRails.ceilingRate}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Min confidence</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              {safetyRails.minConfidence}%
                            </span>
                          </div>
                          <div className="pt-2 border-t border-[var(--border-subtle)] text-xs text-[var(--text-tertiary)]">
                            <div>
                              • Can flex higher on short-lead, high-confidence pressure dates
                            </div>
                            {safetyRails.excludeEventDays && <div>• Event days require review</div>}
                            {safetyRails.requireHealthyData && <div>• Requires healthy data</div>}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Autonomous */}
                  <div
                    className={`flex items-center transition-colors ${
                      autonomyMode === 'autonomous'
                        ? 'bg-[var(--accent-dim)] shadow-[inset_0_0_0_1px_var(--accent)]'
                        : ''
                    }`}
                  >
                    <button
                      role="radio"
                      aria-checked={autonomyMode === 'autonomous'}
                      tabIndex={autonomyRadioTabIndex('autonomous')}
                      aria-disabled={!canEditAutonomy || !autonomyResolved}
                      onClick={() => {
                        void handleQuickAutonomyChange('autonomous');
                      }}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                        autonomyMode === 'autonomous'
                          ? 'text-[var(--accent)]'
                          : !canEditAutonomy
                            ? 'text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                            : autonomyBlockedByTrustState
                              ? 'text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                              : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                      title={
                        autonomyUnavailableTitle ??
                        (!canEditAutonomy
                          ? 'Autonomy changes are admin-only'
                          : autonomyBlockedByTrustState
                            ? trustStateReason
                            : 'Auto-approve eligible rate decisions')
                      }
                    >
                      {autonomyMode === 'autonomous' && (
                        <span
                          aria-hidden="true"
                          className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]"
                        />
                      )}
                      <span>Auto</span>
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          disabled={!canEditAutonomy}
                          className={`hidden md:flex min-h-9 min-w-9 items-center justify-center bg-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                            !canEditAutonomy
                              ? 'text-[var(--text-muted)] opacity-50 cursor-not-allowed'
                              : autonomyMode === 'autonomous'
                                ? 'text-[var(--accent)]'
                                : 'text-[var(--text-muted)] hover:text-[var(--accent)]'
                          }`}
                          title={
                            canEditAutonomy
                              ? 'View auto guardrails'
                              : 'Autonomy changes are admin-only'
                          }
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-64 bg-surface-raised border-[var(--border-color)] p-3"
                        align="end"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-4 h-4 text-accent-muted" />
                          <span className="font-medium text-[var(--text-primary)] text-sm">
                            Auto Safety Rails
                          </span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Base max change</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              ±{safetyRails.maxChangePct}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Rate floor</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              ${safetyRails.floorRate}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Rate ceiling</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              ${safetyRails.ceilingRate}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Min confidence</span>
                            <span className="text-[var(--text-primary)] font-mono">
                              {safetyRails.minConfidence}%
                            </span>
                          </div>
                          <div className="pt-2 border-t border-[var(--border-subtle)] text-xs text-[var(--text-tertiary)]">
                            <div>• Rate actions within rails auto-approved</div>
                            <div>
                              • LOS assist follows its own lane; marketing still requires review
                            </div>
                            <div>
                              • Base cap can flex higher on short-lead, high-confidence pressure
                              dates
                            </div>
                            {safetyRails.requireHealthyData && <div>• Requires healthy data</div>}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={openAutonomySettings}
                className="hidden xl:flex h-10 items-center gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface)] px-3 text-sm font-medium text-[var(--text-muted)] transition-colors hover:border-[var(--accent)] hover:text-[var(--text-primary)]"
                aria-label={`LOS ${losLabel}`}
                title={
                  canEditAutonomy
                    ? 'Open LOS autopush authority'
                    : 'View LOS autopush authority. Changes are admin-only.'
                }
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-label)]">
                  LOS
                </span>
                <span className={losTone}>{losLabel}</span>
              </button>

              {autonomyBlockedByTrustState && (
                <div
                  className="max-w-[9rem] truncate md:max-w-none px-2 py-1 rounded-lg border border-[var(--accent)]/30 bg-[var(--accent-dim)] text-[11px] font-medium text-[var(--accent)]"
                  title={trustStateReason}
                >
                  Cold start: manual only
                </div>
              )}

              {autonomyAdminNotice && (
                <div
                  className="hidden md:block max-w-[26rem] truncate px-2 py-1 rounded-lg border border-[var(--border-color)] bg-[var(--delta-neutral-bg)] text-[11px] font-medium text-[var(--text-secondary)]"
                  title={autonomyAdminNotice}
                >
                  {autonomyAdminNotice}
                </div>
              )}

              {autonomyNotice && (
                <div
                  className="hidden md:block max-w-[24rem] truncate px-2 py-1 rounded-lg border border-[var(--accent)]/30 bg-[var(--accent-dim)] text-[11px] font-medium text-[var(--accent)]"
                  title={autonomyNotice}
                >
                  {autonomyNotice}
                </div>
              )}
            </>
          )}

          {/* Refresh */}
          <button
            type="button"
            onClick={() => void queryClient.invalidateQueries()}
            className="hidden xl:inline-flex min-h-9 min-w-9 items-center justify-center p-2.5 rounded-lg bg-[var(--surface)] border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]"
            title="Refresh data"
            aria-label="Refresh data"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* Theme toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            className="hidden xl:inline-flex min-h-9 min-w-9 items-center justify-center p-2.5 rounded-lg bg-[var(--surface)] border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]"
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Bug Report */}
          <div className="hidden xl:block">
            <BugReportButton propertyId={property.id} />
          </div>
        </div>

        {/* Ask Button - centered in remaining space */}
        <div className="hidden md:flex flex-1 justify-center items-center gap-4">
          <SilasButton
            variant="with-seal"
            size="md"
            onClick={handleAskSilas}
            sealTooltip="Ask Silas (Alt+A)"
            endIcon={<SilasSeal size={24} state="full" tooltip="Ask Silas" />}
            ref={askSilasRef}
            glowing={askSilasGlowing}
            data-ask-silas-trigger="true"
            title="Ask Silas (Alt+A)"
          >
            Ask Silas
          </SilasButton>

          <DataOpsStatusButton propertyId={property.id} />
        </div>

        {showRateControls && (
          <div className="ml-auto hidden lg:flex items-center space-x-2 text-[var(--text-muted)]">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{formatDateRange()}</span>
          </div>
        )}

        {/* Overflow menu - low-frequency controls collapse here below xl */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="xl:hidden ml-auto lg:ml-3 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg bg-[var(--surface)] border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] transition-colors"
              aria-label="More controls"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-60 bg-surface-raised border-[var(--border-color)] p-1.5"
            align="end"
          >
            <div className="flex flex-col">
              <button
                type="button"
                onClick={handleAskSilas}
                data-ask-silas-trigger="true"
                className="md:hidden flex w-full min-h-[44px] items-center gap-2 rounded-md px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors"
              >
                <SilasSeal size={16} state="full" tooltip="Ask Silas" />
                Ask Silas
              </button>
              {showRateControls && (
                <button
                  type="button"
                  onClick={openAutonomySettings}
                  className="flex w-full min-h-[44px] items-center gap-2 rounded-md px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  LOS autopush
                  <span className={`ml-auto text-xs ${losTone}`}>{losLabel}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => void queryClient.invalidateQueries()}
                className="flex w-full min-h-[44px] items-center gap-2 rounded-md px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh data
              </button>
              <button
                type="button"
                onClick={toggleTheme}
                className="flex w-full min-h-[44px] items-center gap-2 rounded-md px-3 py-2.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
              <div className="flex min-h-[44px] items-center px-1">
                <BugReportButton propertyId={property.id} />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Autonomy Settings Modal */}
      {showAutonomyModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setShowAutonomyModal(false)}
        >
          <div
            className="bg-[var(--surface-raised)] rounded-xl p-6 w-full max-w-2xl border border-[var(--border-color)] shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">
                Autonomy Settings
              </h2>
              <button
                onClick={() => setShowAutonomyModal(false)}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {autonomyBlockedByTrustState && (
              <div className="mb-6 rounded-lg border border-[var(--accent)]/30 bg-[var(--accent-dim)] px-4 py-3">
                <div className="text-sm font-medium text-[var(--accent)]">Cold start active</div>
                <div className="mt-1 text-sm text-[var(--text-secondary)]">{trustStateReason}</div>
              </div>
            )}

            {autonomyAdminNotice && (
              <div className="mb-4 text-sm text-[var(--text-secondary)]">{autonomyAdminNotice}</div>
            )}
            {autonomyNotice && (
              <div className="mb-4 text-sm text-[var(--accent)]">{autonomyNotice}</div>
            )}

            <div className="mb-6">
              <label className="text-sm text-[var(--text-muted)] mb-3 block">
                Rate Autonomy Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {/* Manual */}
                <button
                  onClick={() => setPendingMode('manual')}
                  disabled={!canEditAutonomy}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                    !canEditAutonomy
                      ? 'border-[var(--border-color)] opacity-50 cursor-not-allowed'
                      : pendingMode === 'manual'
                        ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                        : 'border-[var(--border-color)] hover:border-[var(--accent)]/50'
                  }`}
                >
                  <Eye
                    className={`w-8 h-8 mb-2 ${pendingMode === 'manual' ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
                  />
                  <span
                    className={`font-medium ${pendingMode === 'manual' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Manual
                  </span>
                  <span className="text-xs text-[var(--text-muted)] mt-1">
                    Review all decisions
                  </span>
                </button>

                {/* Hybrid */}
                <button
                  onClick={() => setPendingMode('hybrid')}
                  disabled={!canEditAutonomy || autonomyBlockedByTrustState}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                    !canEditAutonomy || autonomyBlockedByTrustState
                      ? 'border-[var(--border-color)] opacity-50 cursor-not-allowed'
                      : pendingMode === 'hybrid'
                        ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                        : 'border-[var(--border-color)] hover:border-[var(--accent)]/50'
                  }`}
                >
                  <AlertTriangle
                    className={`w-8 h-8 mb-2 ${pendingMode === 'hybrid' ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
                  />
                  <span
                    className={`font-medium ${pendingMode === 'hybrid' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Hybrid
                  </span>
                  <span className="text-xs text-[var(--text-muted)] mt-1">
                    Auto-approve safe ones
                  </span>
                </button>

                {/* Autonomous */}
                <button
                  onClick={() => setPendingMode('autonomous')}
                  disabled={!canEditAutonomy || autonomyBlockedByTrustState}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                    !canEditAutonomy || autonomyBlockedByTrustState
                      ? 'border-[var(--border-color)] opacity-50 cursor-not-allowed'
                      : pendingMode === 'autonomous'
                        ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                        : 'border-[var(--border-color)] hover:border-[var(--accent)]/50'
                  }`}
                >
                  <Shield
                    className={`w-8 h-8 mb-2 ${pendingMode === 'autonomous' ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
                  />
                  <span
                    className={`font-medium ${pendingMode === 'autonomous' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Autonomous
                  </span>
                  <span className="text-xs text-[var(--text-muted)] mt-1">Auto-approve rates</span>
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="text-sm text-[var(--text-muted)] mb-3 block">
                LOS Autonomy Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setPendingLosMode('manual')}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                    pendingLosMode === 'manual'
                      ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                      : 'border-[var(--border-color)] hover:border-[var(--accent)]/50'
                  }`}
                >
                  <Eye
                    className={`w-8 h-8 mb-2 ${pendingLosMode === 'manual' ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
                  />
                  <span
                    className={`font-medium ${pendingLosMode === 'manual' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Manual
                  </span>
                  <span className="text-xs text-[var(--text-muted)] mt-1">Review all LOS</span>
                </button>

                <button
                  onClick={() => setPendingLosMode('hybrid')}
                  disabled={autonomyBlockedByTrustState}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                    pendingLosMode === 'hybrid'
                      ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                      : autonomyBlockedByTrustState
                        ? 'border-[var(--border-color)] opacity-50 cursor-not-allowed'
                        : 'border-[var(--border-color)] hover:border-[var(--accent)]/50'
                  }`}
                >
                  <AlertTriangle
                    className={`w-8 h-8 mb-2 ${pendingLosMode === 'hybrid' ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
                  />
                  <span
                    className={`font-medium ${pendingLosMode === 'hybrid' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Hybrid
                  </span>
                  <span className="text-xs text-[var(--text-muted)] mt-1">Safe LOS assist</span>
                </button>

                <button
                  onClick={() => setPendingLosMode('autonomous')}
                  disabled={autonomyBlockedByTrustState}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                    pendingLosMode === 'autonomous'
                      ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                      : autonomyBlockedByTrustState
                        ? 'border-[var(--border-color)] opacity-50 cursor-not-allowed'
                        : 'border-[var(--border-color)] hover:border-[var(--accent)]/50'
                  }`}
                >
                  <Shield
                    className={`w-8 h-8 mb-2 ${pendingLosMode === 'autonomous' ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
                  />
                  <span
                    className={`font-medium ${pendingLosMode === 'autonomous' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Autonomous
                  </span>
                  <span className="text-xs text-[var(--text-muted)] mt-1">High-confidence LOS</span>
                </button>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-2">
                Hybrid LOS automation is intentionally conservative: close-in relaxations plus
                high-confidence event/compression tightenings.
              </p>
            </div>

            {/* Safety Rails Section - Only show for Hybrid/Autonomous */}
            {(pendingMode === 'hybrid' || pendingMode === 'autonomous') && (
              <div className="mb-6 p-4 bg-[var(--surface)]/50 rounded-lg border border-[var(--border-color)]">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-[var(--accent)]" />
                  <h3 className="text-[var(--text-primary)] font-medium">Safety Rails</h3>
                </div>

                {/* Current Rails Summary */}
                <div className="mb-4 p-3 bg-[var(--surface)] rounded-lg border border-[var(--border-color)]">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-[var(--accent)]" />
                    <span className="text-[var(--text-muted)]">Auto-approval enabled with:</span>
                  </div>
                  <p className="text-[var(--text-primary)] text-sm mt-1">
                    {getSafetyRailsSummary()}
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Max Change Percentage */}
                  <div>
                    <label className="flex items-center justify-between text-sm text-[var(--text-muted)] mb-2">
                      <span>Max change per update</span>
                      <span className="text-[var(--text-primary)] font-mono">
                        ±{safetyRails.maxChangePct}%
                      </span>
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="25"
                      step="5"
                      value={safetyRails.maxChangePct}
                      onChange={(e) =>
                        setSafetyRails((prev) => ({
                          ...prev,
                          maxChangePct: Number(e.target.value),
                        }))
                      }
                      className="w-full h-2 bg-[var(--surface)] rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                    />
                    <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
                      <span>±5%</span>
                      <span>±25%</span>
                    </div>
                  </div>

                  {/* Floor and Ceiling Rates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[var(--text-muted)] mb-2 block">
                        Floor guardrail
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
                          $
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="500"
                          value={safetyRails.floorRate}
                          onChange={(e) =>
                            setSafetyRails((prev) => ({
                              ...prev,
                              floorRate: Number(e.target.value),
                            }))
                          }
                          className="w-full bg-[var(--surface)] border border-[var(--border-color)] rounded-lg px-3 py-2 pl-7 text-[var(--text-primary)] font-mono focus:outline-none focus:border-[var(--accent)]"
                        />
                      </div>
                      <span className="text-xs text-[var(--text-muted)]">Minimum rate</span>
                    </div>
                    <div>
                      <label className="text-sm text-[var(--text-muted)] mb-2 block">
                        Ceiling guardrail
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
                          $
                        </span>
                        <input
                          type="number"
                          min="100"
                          max="1000"
                          value={safetyRails.ceilingRate}
                          onChange={(e) =>
                            setSafetyRails((prev) => ({
                              ...prev,
                              ceilingRate: Number(e.target.value),
                            }))
                          }
                          className="w-full bg-[var(--surface)] border border-[var(--border-color)] rounded-lg px-3 py-2 pl-7 text-[var(--text-primary)] font-mono focus:outline-none focus:border-[var(--accent)]"
                        />
                      </div>
                      <span className="text-xs text-[var(--text-muted)]">Maximum rate</span>
                    </div>
                  </div>

                  {/* Min Confidence Threshold */}
                  <div>
                    <label className="flex items-center justify-between text-sm text-[var(--text-muted)] mb-2">
                      <span>Min confidence threshold</span>
                      <span className="text-[var(--text-primary)] font-mono">
                        {safetyRails.minConfidence}%
                      </span>
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="95"
                      step="5"
                      value={safetyRails.minConfidence}
                      onChange={(e) =>
                        setSafetyRails((prev) => ({
                          ...prev,
                          minConfidence: Number(e.target.value),
                        }))
                      }
                      className="w-full h-2 bg-[var(--surface)] rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                    />
                    <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
                      <span>50%</span>
                      <span>95%</span>
                    </div>
                  </div>

                  {/* Checkbox options */}
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={safetyRails.requireHealthyData}
                        onChange={(e) =>
                          setSafetyRails((prev) => ({
                            ...prev,
                            requireHealthyData: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--surface)] text-[var(--accent)] focus:ring-[var(--accent)] focus:ring-offset-0"
                      />
                      <span className="text-[var(--text-secondary)] text-sm">
                        Only approve if data health is green
                      </span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={safetyRails.excludeEventDays}
                        onChange={(e) =>
                          setSafetyRails((prev) => ({
                            ...prev,
                            excludeEventDays: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--surface)] text-[var(--accent)] focus:ring-[var(--accent)] focus:ring-offset-0"
                      />
                      <span className="text-[var(--text-secondary)] text-sm">
                        Exclude event days (require manual review)
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Notification Setting */}
            <label className="flex items-center space-x-3 p-3 bg-[var(--surface)] rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={notifyOnAuto}
                onChange={(e) => setNotifyOnAuto(e.target.checked)}
                disabled={!canEditAutonomy}
                className="w-5 h-5 rounded border-[var(--border-color)] bg-[var(--surface)] text-[var(--accent)] focus:ring-[var(--accent)] focus:ring-offset-0"
              />
              <span className="text-[var(--text-secondary)]">
                Send notifications for auto-approved changes
              </span>
            </label>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAutonomyModal(false)}
                className="px-4 py-2 rounded-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]"
                disabled={isSaving}
              >
                Cancel
              </button>
              <SilasButton
                variant="primary"
                size="md"
                onClick={handleSaveSettings}
                loading={isSaving}
                disabled={!canEditAutonomy}
              >
                {isSaving
                  ? 'Saving autonomy settings'
                  : canEditAutonomy
                    ? 'Save autonomy settings'
                    : 'Admin only'}
              </SilasButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
